import { ActionDialog, Loading, Divider, Notification } from "@/components"
import { ClientProfileInfoData, TagDataProps } from "@/protocols/clientCatalog"
import { Grid, Avatar, Typography, Divider as MuiDivider, Box, IconButton, Switch, withStyles, Tooltip, InputLabel, TextField } from "@material-ui/core"
import React, { ReactNode, useState } from "react"
import useStyles from "@/pages/Admin/NewClientCatalog/ClientProfileInfo/styles"
import ApiService from "@/services/Api"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import ErrorHandlerService from "@/services/ErrorHandler"
import ClientTags from "@/pages/Admin/NewClientCatalog/ClientProfileInfo/Tags"
import ClientCustomFields from "@/pages/Admin/NewClientCatalog/ClientProfileInfo/CustomFields"
import DatesAndIdentifier from "@/pages/Admin/NewClientCatalog/ClientProfileInfo/DatesAndIdentifier"
import ClientTabNavigation from "@/pages/Admin/NewClientCatalog/ClientProfileInfo/ClientTabNavigation"
import { InfoOutlined as InfoIcon, Edit as EditIcon } from "@material-ui/icons"
import ClientNotesTable from "@/pages/Admin/NewClientCatalog/ClientProfileInfo/Notes"
import PhoneNumberRedirect from "@/components/PhoneNumberRedirect"
import HandleResponsibleAttendant from "@/pages/Admin/NewClientCatalog/ClientProfileInfo/HandleResponsibleAttendant"
import useCustomStyles from "@/styles/custom"
import ClientProfileInfoSkeleton from "@/skeletons/Admin/ClientProfileInfoSkeleton"

export type OnDataChangeFn = (id: number, data: Partial<ClientProfileInfoData>) => void

type ClientProfileInfoProps = {
	children: ReactNode
	clientId: number
	onDataChange: OnDataChangeFn
	customerTags: TagDataProps[]
}
type ClientChangeData = {
	email: string
	nickname: string
}

const ClientProfileInfo: React.FC<ClientProfileInfoProps> = ({ children, customerTags, clientId, onDataChange }) => {
	const [openDialog, setOpenDialog] = useState(false)
	const [client, setClient] = useState<ClientProfileInfoData>({} as ClientProfileInfoData)
	const [loading, setLoading] = useState(false)
	const [isAutomaticMessages, setHandleAutomaticMessages] = useState<boolean>(client.optIn)
	const [clientDisplayInfo, setClientDisplayInfo] = useState("CAMPOS PERSONALIZADOS")
	const [opeEditDialog, setOpenEditDialog] = useState(false)
	const [clientDataFields, setClientDataFields] = useState<ClientChangeData>({} as ClientChangeData)
	const { triggerValidation } = useValidation()

	const getClientProfileInfoData = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get(`/clients/${clientId}/profile`)
			setClient(data)
			setHandleAutomaticMessages(data.optIn)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
		setLoading(false)
	}

	const handleUpdateClientEmail = async () => {
		try {
			const { data } = await ApiService.post(
				`/clients/${clientId}/email`,
				{
					email: clientDataFields.email
				}
			)
			const { updatedContact } = data
			const contacts = {
				email: updatedContact.data.email
			}
			setClient(lastState => {
				return {
					...lastState,
					contacts: contacts
				}
			})
			onDataChange(client.id, { contacts: { email: updatedContact.data.email } })
		} catch (err) {
			triggerValidation(err as ErrorType)
		}
	}

	const handleUpdateClientOptIn = async () => {
		try {
			const payload = {
				nickname: client.nickname,
				accepts_automatic_messages: !isAutomaticMessages
			}
			const { data } = await ApiService.put(`/clients/${client.id}`, payload)
			const { updatedClient } = data
			onDataChange(client.id, { optIn: updatedClient.accepts_automatic_messages })
		} catch (error) {
			triggerValidation(error as ErrorType)
		}
	}

	const handleUpdateClientNickname = async () => {
		try {
			const { data } = await ApiService.put(
				`/clients/${clientId}`,
				{ nickname: clientDataFields.nickname }
			)
			const { updatedClient } = data
			setClient(lastState => {
				return {
					...lastState,
					nickname: updatedClient.nickname
				}
			})
			onDataChange(client.id, { nickname: updatedClient.nickname })
		} catch (err) {
			triggerValidation(err as ErrorType)
		}
	}

	const handleOptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setHandleAutomaticMessages(event.target.checked)
		handleUpdateClientOptIn()
	}

	const handleOpenDialog = async () => {
		setLoading(true)
		setOpenDialog(true)
		await getClientProfileInfoData()
		setLoading(false)
	}

	const handleCloseDialog = () => {
		setOpenDialog(false)
	}

	const handleOpenEditClientNameAndEmailDialog = () => {
		setOpenEditDialog(true)
	}

	const handleCloseEditClientNameAndEmailDialog = () => {
		setOpenEditDialog(false)
	}

	const handleUpdateClientPersonalData = () => {
		if (clientDataFields.email !== client.contacts?.email) {
			handleUpdateClientEmail()
		}
		if (clientDataFields.nickname !== client.nickname) {
			handleUpdateClientNickname()
		}

		Notification.success({ message: "Cliente atualizado." })

		setOpenEditDialog(false)
	}

	const handleChangeFields = async (value: string | number, field: keyof ClientChangeData) => {
		setClientDataFields(lastState => ({
			...lastState,
			[field]: value
		}))
	}

	const customClasses = useCustomStyles()
	const classes = useStyles()

	const CustomSwitch = withStyles({
		track: {
			backgroundColor: "#D32F2F"
		},
		colorSecondary: {
			color: "#D32F2F"

		}
	})(Switch)

	return (
		<>
			<div
				onClick={handleOpenDialog}
			>
				{children}
			</div>
			<ActionDialog
				hideCloseButton={true}
				onClose={handleCloseDialog}
				openDialog={openDialog}
				title={"PERFIL DO CONTATO"}
				fullWidth
				maxWidth="lg"
				dialogContentClassName={classes.resetDialogContentPadding}
			>
				<Loading loading={loading} customLoadingElement={<ClientProfileInfoSkeleton />}>
					{!loading && <Grid container direction="row">
						<Grid item style={{ width: 348 }}>
							<Grid item>
								<Grid container className={classes.clientIdentification}>
									<Grid item className={classes.avatarContainer}>
										<Avatar className={classes.avatar} src={client?.pictureUrl} />
									</Grid>
									<Grid item>
										{client.contacts?.phone && <PhoneNumberRedirect customizedClass={classes.phoneNumberSize} phoneNumber={client.contacts?.phone} />}
										<Typography className={classes.clientName}>
											~{client.name}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
							<MuiDivider orientation="horizontal" className={classes.containerFillSize} />
							<Grid container direction="column" alignItems="flex-start" style={{ padding: 32 }}>
								<Grid item className={classes.containerFillSize}>
									<Grid container direction="column">
										<Grid item>
											<Grid container direction="row" alignItems="center" justifyContent="space-between">
												<Typography className={classes.clientNickname}>
													{client.nickname}
												</Typography>
												<IconButton onClick={handleOpenEditClientNameAndEmailDialog} style={{ paddingRight: 0 }}>
													<EditIcon />
												</IconButton>
											</Grid>
										</Grid>
										<Grid item>
											<Typography className={classes.clientEmail}>
												{client.contacts?.email}
											</Typography>
										</Grid>
									</Grid>
								</Grid>
								<Divider orientation="horizontal" size={2} />
								<Grid item className={classes.containerFillSize}>
									<Grid container direction="row" justifyContent="space-between" >
										<Grid item>
											<Box className={classes.variableBox}>
												<Typography className={classes.clientOptIn}>Opt-in</Typography>
												<Tooltip title={"O cliente participará de uma lista de contatos com autorização para receber suas notificações gerais. Porém, quando desativado, você só poderá enviar mensagem através do Inbox."}>
													<InfoIcon className={classes.customInfoIcon} />
												</Tooltip>
											</Box>
										</Grid>
										<Grid item>
											<CustomSwitch
												defaultChecked={isAutomaticMessages}
												size="small"
												onChange={handleOptChange}
												inputProps={{ "aria-label": "controlled" }}
											/>
										</Grid>
									</Grid>
								</Grid>
							</Grid>

							<MuiDivider orientation="horizontal" className={classes.containerFillSize} />

							<HandleResponsibleAttendant clientId={client.id} clientAttendantManager={client?.accountManagerUser} inboxChannelChatId={client.inboxChannelChat?.id} loading={loading} />

							<MuiDivider orientation="horizontal" className={classes.containerFillSize} />

							{customerTags && <ClientTags availableTags={customerTags} clientId={client.id} clientAssociatedTags={client.tags} inboxChannelChat={client.inboxChannelChat} onDataChange={onDataChange} />}

							<DatesAndIdentifier {...client} />
						</Grid>

						<MuiDivider orientation="vertical" flexItem={true} />

						<Grid item className={classes.clientPersonalDataView}>

							<ClientTabNavigation value={clientDisplayInfo} onChange={setClientDisplayInfo} options={["CAMPOS PERSONALIZADOS", "NOTAS"]} />

							{clientDisplayInfo === "CAMPOS PERSONALIZADOS" &&

								<ClientCustomFields onDataChange={onDataChange} clientId={client.id} customFieldGroups={client.customFieldGroups} />
							}

							{clientDisplayInfo === "NOTAS" &&

								<ClientNotesTable inboxChannelChat={client.inboxChannelChat.id} clientId={client.id} isExpandedNotes={false} loading={false} />
							}
						</Grid>

					</Grid>}

				</Loading>
			</ActionDialog>

			<ActionDialog
				onClose={handleCloseEditClientNameAndEmailDialog}
				openDialog={opeEditDialog}
				title={"Alterar dados do cliente"}
				fullWidth
				saveText={"Salvar"}
				cancelText="Cancelar"
				onSave={handleUpdateClientPersonalData}
			>
				<Grid container>
					<Grid xs={12}>
						<Grid
							item
							xs
						>
							<InputLabel className={customClasses.inputLabel}>Nome:</InputLabel>
							<Divider size={2} orientation="horizontal" />

							<TextField
								name="client_name"
								variant="outlined"
								placeholder={"Digite o novo nome"}
								fullWidth
								defaultValue={client.nickname}
								value={clientDataFields.nickname}
								onChange={({ target }) => handleChangeFields(target.value, "nickname")}
							/>
						</Grid>
						<Divider size={3} orientation="horizontal" />
						<Grid
							item
							xs
						>
							<InputLabel className={customClasses.inputLabel}>Email:</InputLabel>
							<Divider size={2} orientation="horizontal" />

							<TextField
								name="email_field"
								variant="outlined"
								placeholder={"Digite o novo email"}
								fullWidth
								defaultValue={client.contacts && client.contacts.email}
								value={clientDataFields.email}
								onChange={({ target }) => handleChangeFields(target.value, "email")}
							/>
						</Grid>
					</Grid>
				</Grid>
			</ActionDialog>
		</>
	)
}

export default ClientProfileInfo
